import { FC, useEffect } from 'react';
import type { FieldAttributes, FieldProps } from 'formik';
import { DialogTitle, Description as DialogDescription } from '@headlessui/react';
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik';
import { useTracking } from '@/components/tracking-provider';
import Turnstile, { useTurnstile } from 'react-turnstile';
import { AuthResultSuccess, storeAuthSession, markNewSignup } from '@/app/utils/authentication';
import {
  performSignup,
  signupValidationSchema,
  isAuthError,
  isAuthSuccess,
} from '@/app/utils/auth-api';

const InputFieldset = ({
  name,
  label,
  ...rest
}: FieldAttributes<{ label: string; autoComplete?: string }>) => (
  <div className="form-control">
    <label htmlFor={name}>
      <span>{label}</span>
      <ErrorMessage name={name} className="form-field-error" component="span" />
    </label>
    <Field id={name} name={name} {...rest} className="form-input" />
  </div>
);

interface SignupFormValues {
  base: string; // used for global error messages
  name: string;
  email: string;
  password: string;
  passwordConfirm: string;
  newsletter: boolean;
  recaptcha: string;
  turnstile: string;
}

interface TurnstileInputComponentProps {
  siteKey: string;
  theme: 'auto' | 'light' | 'dark' | undefined;
}

const TurnstileField: FC<
  TurnstileInputComponentProps & FieldProps<{ value: string }, SignupFormValues>
> = ({
  siteKey,
  theme,
  form: { touched, errors, setFieldValue }, // values, dirty, isValid, status, etc.
}) => {
  return (
    <div className="h-[78px]">
      <Turnstile
        sitekey={siteKey}
        theme={theme}
        action="signup"
        onVerify={(value) => setFieldValue('turnstile', value)}
      />
      {errors.turnstile && touched.turnstile ? (
        <div className="form-field-error">{errors.turnstile}</div>
      ) : null}
    </div>
  );
};

interface SignupFormProps {
  onSuccess?: (result: AuthResultSuccess) => void;
  onSwitchToLogin?: () => void;
  compact?: boolean;
  hideHeader?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function SignupForm({ onSuccess, onSwitchToLogin, compact, hideHeader }: SignupFormProps) {
  const tracker = useTracking();
  const turnstileTheme = 'light';
  const turnstileSiteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  const turnstile = useTurnstile();
  // onmount only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => tracker.trackEvent('Signup Begin', { state_name: 'index.next' }), []);

  return (
    <>
      {!hideHeader && (
        <>
          <DialogTitle className="mb-2">
            <span className="h3 font-merriweather">Create account</span>
          </DialogTitle>
          <DialogDescription className="mb-6">
            <span className="text-sm">
              Enter your information below to sign up for a free Penzu account.
            </span>
            <br />
            <span className="text-sm">
              Already have an account?{' '}
              <button
                type="button"
                onClick={onSwitchToLogin}
                className="underline text-slate-800 hover:text-slate-600 font-medium cursor-pointer"
              >
                Log in here.
              </button>
            </span>
          </DialogDescription>
        </>
      )}
      <Formik
        initialValues={{
          base: '',
          name: '',
          email: '',
          password: '',
          passwordConfirm: '',
          newsletter: true,
          recaptcha: '',
          turnstile: '',
        }}
        validationSchema={signupValidationSchema}
        onSubmit={async (values, actions: FormikHelpers<SignupFormValues>) => {
          const result = await performSignup({
            email: values.email,
            password: values.password,
            // retypePassword: values.passwordConfirm, // confirm-password field disabled
            fullName: values.name,
            turnstile: values.turnstile,
            receiveMarketingEmails: values.newsletter,
          });

          if (isAuthError(result)) {
            actions.setSubmitting(false);
            actions.setErrors({ base: `${result.error.title}: ${result.error.message}` });
            setTimeout(() => {
              // This clears the error message, so give it a short delay
              turnstile.reset();
            }, 1000);
          } else if (isAuthSuccess(result)) {
            try {
              storeAuthSession(result, tracker, 'Signup Complete', {
                'pz-registered': 'true',
              });
              // Mark that the user signed up during this visit so a subsequent
              // checkout can route them to /welcome after purchase.
              markNewSignup();
            } catch (error) {
              console.error('Unexpected error completing signup:', error);
            }

            setTimeout(() => {
              if (onSuccess) {
                onSuccess(result);
              } else {
                window.location.href = '/welcome';
              }
            }, 500);
          }
        }}
        onReset={() => turnstile.reset()}
      >
        {({ isSubmitting, isValid, dirty, errors }) => (
          <Form>
            {errors.base && <div className="form-error">{errors.base}</div>}
            <fieldset className="max-w-sm md:max-w-full md:w-lg flex flex-col space-y-4 mb-4">
              <InputFieldset
                name="name"
                label="Full name"
                type="text"
                placeholder="eg. Jessica Smith"
              />
              <InputFieldset
                name="email"
                label="Email"
                type="email"
                placeholder="eg. you@example.com"
                autoComplete="username"
              />
              <InputFieldset
                name="password"
                label="Create a password"
                type="password"
                placeholder="Create a strong password"
                autoComplete="new-password"
              />
              {/* Confirm-password field temporarily disabled (see app/utils/auth-api.ts)
              <InputFieldset
                name="passwordConfirm"
                label="Confirm password"
                type="password"
                placeholder="Re-type your password"
                autoComplete="new-password"
              />
              */}
            </fieldset>
            <fieldset className="max-w-sm md:w-[32rem] flex flex-col space-y-4 mb-6">
              <div className="">
                <Field id="newsletter" name="newsletter" type="checkbox" />
                <label htmlFor="newsletter" className="ml-2 text-sm text-slate-600">
                  Send me occasional updates from Penzu
                </label>
              </div>

              <Field
                name="turnstile"
                component={TurnstileField}
                siteKey={turnstileSiteKey}
                theme={turnstileTheme}
              />
            </fieldset>

            <div>
              <button
                type="submit"
                className="btn text-white bg-blue-500 hover:bg-blue-700 w-full"
                disabled={isSubmitting || !isValid || !dirty}
              >
                Create account
              </button>
            </div>
            <div className="mt-4 text-sm text-slate-600">
              By signing up you agree to our{' '}
              <a
                href="/terms"
                target="_blank"
                className="underline text-slate-800 hover:text-slate-600"
              >
                Terms of Use
              </a>{' '}
              and{' '}
              <a
                href="/privacy"
                target="_blank"
                className="underline text-slate-800 hover:text-slate-600"
              >
                Privacy Policy
              </a>
              .
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
